import { useEffect, useId, useMemo, useState, type ReactNode } from "react";
import { Check, CircleAlert, Plus, X } from "lucide-react";
import {
  availableMatchStrategies,
  defaultBehavior,
  defaultSchedule,
  proposeFieldMappings,
  reconcileFieldMappings,
  validateConfigureSync,
  type ConnectorField,
  type ConnectorSchema,
  type DayOfWeek,
  type FieldMapping,
  type MatchStrategy,
  type SyncFrequency,
  type SyncScheduleConfig,
} from "@attendee-sync/connectors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { SimpleTooltip } from "@/components/ui/tooltip";
import { StatusBadge } from "@/components/status/status-badge";
import { api } from "@/lib/api";
import { cn } from "@/lib/cn";
import { useOrg } from "@/lib/org-context";
import { clearWizard } from "@/features/wizard/wizard-state";
import { useExitWizard, useWizardNavigate } from "@/lib/wizard-navigation";
import {
  configureSchemaKey,
  loadWizard,
  saveWizard,
  type WizardConfigureState,
} from "./wizard-state";

const DAYS: Array<{ id: DayOfWeek; label: string }> = [
  { id: "mon", label: "Mon" },
  { id: "tue", label: "Tue" },
  { id: "wed", label: "Wed" },
  { id: "thu", label: "Thu" },
  { id: "fri", label: "Fri" },
  { id: "sat", label: "Sat" },
  { id: "sun", label: "Sun" },
];

const FREQUENCY_OPTIONS: Array<{ value: SyncFrequency; label: string }> = [
  { value: "after_event", label: "After Each Meeting or Event" },
  { value: "once", label: "Once" },
  { value: "hourly", label: "Hourly" },
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "custom", label: "Custom" },
];

function selectClassName(extra?: string) {
  return cn(
    "flex h-12 w-full rounded-rs-sm border border-rs-border bg-rs-elevated px-4 text-sm text-rs-ink",
    "disabled:cursor-not-allowed disabled:opacity-50",
    extra,
  );
}

function WizardStepFooter({
  onBack,
  primary,
}: {
  onBack?: () => void;
  primary?: ReactNode;
}) {
  const exitWizard = useExitWizard();
  const { orgId } = useOrg();
  return (
    <div className="rs-wizard-step-footer">
      <Button
        variant="link"
        className="rs-wizard-later"
        onClick={() => {
          clearWizard(orgId);
          exitWizard();
        }}
      >
        I&apos;ll Do This Later
      </Button>
      <div className="rs-wizard-step-footer-actions">
        {onBack ? (
          <Button variant="secondary" onClick={onBack}>
            Back
          </Button>
        ) : null}
        {primary}
      </div>
    </div>
  );
}

function MappingStatus({ mapping }: { mapping: FieldMapping }) {
  if (mapping.ignored) {
    return <StatusBadge tone="neutral">Ignored</StatusBadge>;
  }
  switch (mapping.status) {
    case "matched":
      return (
        <StatusBadge tone="ready">
          <Check className="size-3" aria-hidden /> Matched
        </StatusBadge>
      );
    case "suggested":
      return <StatusBadge tone="scheduled">Suggested</StatusBadge>;
    case "required":
      return (
        <StatusBadge tone="failed">
          <CircleAlert className="size-3" aria-hidden /> Required
        </StatusBadge>
      );
    default:
      return (
        <StatusBadge tone="needs_configuration">
          <CircleAlert className="size-3" aria-hidden /> Unmapped
        </StatusBadge>
      );
  }
}

function SearchableFieldSelect({
  id,
  value,
  options,
  placeholder,
  disabled,
  onChange,
}: {
  id: string;
  value: string | null;
  options: ConnectorField[];
  placeholder: string;
  disabled?: boolean;
  onChange: (next: string | null) => void;
}) {
  const [query, setQuery] = useState("");
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (f) =>
        f.label.toLowerCase().includes(q) ||
        f.key.toLowerCase().includes(q) ||
        (f.aliases ?? []).some((a) => a.toLowerCase().includes(q)),
    );
  }, [options, query]);

  return (
    <div className="space-y-1.5">
      <Input
        type="search"
        value={query}
        disabled={disabled}
        placeholder="Search Destination Fields"
        aria-label="Search Destination Fields"
        className="h-9"
        onChange={(e) => setQuery(e.target.value)}
      />
      <select
        id={id}
        className={selectClassName()}
        value={value ?? ""}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value || null)}
      >
        <option value="">{placeholder}</option>
        {filtered.map((f) => (
          <option key={f.key} value={f.key}>
            {f.label}
            {f.required ? " *" : ""}
          </option>
        ))}
      </select>
    </div>
  );
}

function ScheduleSection({
  schedule,
  sourceSchema,
  errors,
  onChange,
}: {
  schedule: SyncScheduleConfig;
  sourceSchema: ConnectorSchema;
  errors: string[];
  onChange: (next: SyncScheduleConfig) => void;
}) {
  const supportsEvent = Boolean(
    sourceSchema.capabilities.supportsEventTriggeredSync,
  );
  const frequencies = FREQUENCY_OPTIONS.filter(
    (opt) => opt.value !== "after_event" || supportsEvent,
  );

  function set<K extends keyof SyncScheduleConfig>(
    key: K,
    value: SyncScheduleConfig[K],
  ) {
    onChange({ ...schedule, [key]: value });
  }

  return (
    <section
      className="rs-wizard-configure-section"
      aria-labelledby="configure-schedule-heading"
    >
      <h3 id="configure-schedule-heading">Sync Schedule</h3>
      <div className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="configure-frequency">Frequency</Label>
          <select
            id="configure-frequency"
            className={selectClassName()}
            value={schedule.frequency}
            onChange={(e) => {
              const frequency = e.target.value as SyncFrequency;
              const next: SyncScheduleConfig = {
                ...schedule,
                frequency,
                timezone:
                  schedule.timezone ||
                  Intl.DateTimeFormat().resolvedOptions().timeZone ||
                  "UTC",
              };
              if (frequency === "after_event") {
                next.eventDelayMinutes = schedule.eventDelayMinutes ?? 15;
                next.retryIfUnavailable = schedule.retryIfUnavailable !== false;
              }
              if (frequency === "hourly" || frequency === "daily") {
                next.interval = schedule.interval ?? 1;
                next.time = schedule.time || "09:00";
              }
              if (frequency === "weekly") {
                next.daysOfWeek = schedule.daysOfWeek?.length
                  ? schedule.daysOfWeek
                  : ["mon"];
                next.time = schedule.time || "09:00";
              }
              if (frequency === "monthly") {
                next.dayOfMonth = schedule.dayOfMonth ?? 1;
                next.time = schedule.time || "09:00";
              }
              if (frequency === "custom") {
                next.interval = schedule.interval ?? 15;
                next.customUnit = schedule.customUnit ?? "minutes";
              }
              onChange(next);
            }}
          >
            {frequencies.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {schedule.frequency === "after_event" ? (
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="configure-event-delay">Run Sync</Label>
              <div className="flex flex-wrap items-center gap-2 text-sm text-rs-ink">
                <Input
                  id="configure-event-delay"
                  type="number"
                  min={0}
                  max={1440}
                  className="w-24"
                  value={schedule.eventDelayMinutes ?? 15}
                  onChange={(e) =>
                    set("eventDelayMinutes", Number(e.target.value))
                  }
                />
                <span>minutes after the event ends</span>
              </div>
            </div>
            <label className="flex items-start gap-2 text-sm text-rs-ink">
              <input
                type="checkbox"
                className="mt-1 size-4 rounded border-rs-border"
                checked={schedule.retryIfUnavailable !== false}
                onChange={(e) => set("retryIfUnavailable", e.target.checked)}
              />
              <span>Retry if attendee data is not immediately available</span>
            </label>
          </div>
        ) : null}

        {schedule.frequency === "once" ? (
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="space-y-1.5">
              <Label htmlFor="configure-once-date">Date</Label>
              <Input
                id="configure-once-date"
                type="date"
                value={schedule.date ?? ""}
                onChange={(e) => set("date", e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="configure-once-time">Time</Label>
              <Input
                id="configure-once-time"
                type="time"
                value={schedule.time ?? ""}
                onChange={(e) => set("time", e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="configure-once-tz">Time Zone</Label>
              <Input
                id="configure-once-tz"
                value={schedule.timezone ?? ""}
                onChange={(e) => set("timezone", e.target.value)}
              />
            </div>
          </div>
        ) : null}

        {schedule.frequency === "hourly" ? (
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="space-y-1.5">
              <Label htmlFor="configure-hourly-interval">Every</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="configure-hourly-interval"
                  type="number"
                  min={1}
                  className="w-24"
                  value={schedule.interval ?? 1}
                  onChange={(e) => set("interval", Number(e.target.value))}
                />
                <span className="text-sm text-rs-ink-muted">hour(s)</span>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="configure-hourly-time">Starting At</Label>
              <Input
                id="configure-hourly-time"
                type="time"
                value={schedule.time ?? "09:00"}
                onChange={(e) => set("time", e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="configure-hourly-tz">Time Zone</Label>
              <Input
                id="configure-hourly-tz"
                value={schedule.timezone ?? ""}
                onChange={(e) => set("timezone", e.target.value)}
              />
            </div>
          </div>
        ) : null}

        {schedule.frequency === "daily" ? (
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="space-y-1.5">
              <Label htmlFor="configure-daily-interval">Every</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="configure-daily-interval"
                  type="number"
                  min={1}
                  className="w-24"
                  value={schedule.interval ?? 1}
                  onChange={(e) => set("interval", Number(e.target.value))}
                />
                <span className="text-sm text-rs-ink-muted">day(s)</span>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="configure-daily-time">At</Label>
              <Input
                id="configure-daily-time"
                type="time"
                value={schedule.time ?? "09:00"}
                onChange={(e) => set("time", e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="configure-daily-tz">Time Zone</Label>
              <Input
                id="configure-daily-tz"
                value={schedule.timezone ?? ""}
                onChange={(e) => set("timezone", e.target.value)}
              />
            </div>
          </div>
        ) : null}

        {schedule.frequency === "weekly" ? (
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Days</Label>
              <div className="flex flex-wrap gap-2">
                {DAYS.map((day) => {
                  const selected =
                    schedule.daysOfWeek?.includes(day.id) ?? false;
                  return (
                    <button
                      key={day.id}
                      type="button"
                      className={cn(
                        "rounded-rs-sm border px-3 py-1.5 text-sm",
                        selected
                          ? "border-rs-accent bg-rs-accent-soft text-rs-accent-ink"
                          : "border-rs-border bg-rs-elevated text-rs-ink-muted",
                      )}
                      aria-pressed={selected}
                      onClick={() => {
                        const current = new Set(schedule.daysOfWeek ?? []);
                        if (current.has(day.id)) current.delete(day.id);
                        else current.add(day.id);
                        set("daysOfWeek", [...current] as DayOfWeek[]);
                      }}
                    >
                      {day.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="configure-weekly-time">At</Label>
                <Input
                  id="configure-weekly-time"
                  type="time"
                  value={schedule.time ?? "09:00"}
                  onChange={(e) => set("time", e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="configure-weekly-tz">Time Zone</Label>
                <Input
                  id="configure-weekly-tz"
                  value={schedule.timezone ?? ""}
                  onChange={(e) => set("timezone", e.target.value)}
                />
              </div>
            </div>
          </div>
        ) : null}

        {schedule.frequency === "monthly" ? (
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="space-y-1.5">
              <Label htmlFor="configure-month-day">Day of Month</Label>
              <Input
                id="configure-month-day"
                type="number"
                min={1}
                max={31}
                value={schedule.dayOfMonth ?? 1}
                onChange={(e) => set("dayOfMonth", Number(e.target.value))}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="configure-month-time">At</Label>
              <Input
                id="configure-month-time"
                type="time"
                value={schedule.time ?? "09:00"}
                onChange={(e) => set("time", e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="configure-month-tz">Time Zone</Label>
              <Input
                id="configure-month-tz"
                value={schedule.timezone ?? ""}
                onChange={(e) => set("timezone", e.target.value)}
              />
            </div>
          </div>
        ) : null}

        {schedule.frequency === "custom" ? (
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="space-y-1.5">
              <Label htmlFor="configure-custom-interval">Every</Label>
              <Input
                id="configure-custom-interval"
                type="number"
                min={1}
                value={schedule.interval ?? 15}
                onChange={(e) => set("interval", Number(e.target.value))}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="configure-custom-unit">Unit</Label>
              <select
                id="configure-custom-unit"
                className={selectClassName()}
                value={schedule.customUnit ?? "minutes"}
                onChange={(e) =>
                  set(
                    "customUnit",
                    e.target.value as SyncScheduleConfig["customUnit"],
                  )
                }
              >
                <option value="minutes">Minutes</option>
                <option value="hours">Hours</option>
                <option value="days">Days</option>
              </select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="configure-custom-tz">Time Zone</Label>
              <Input
                id="configure-custom-tz"
                value={schedule.timezone ?? ""}
                onChange={(e) => set("timezone", e.target.value)}
              />
            </div>
          </div>
        ) : null}

        {errors.length > 0 ? (
          <ul className="space-y-1 text-sm text-rs-danger" role="alert">
            {errors.map((err) => (
              <li key={err}>{err}</li>
            ))}
          </ul>
        ) : null}
      </div>
    </section>
  );
}

function MappingRow({
  mapping,
  sourceField,
  destField,
  writableFields,
  availableSources,
  onDestinationChange,
  onIgnore,
  onSourceChange,
}: {
  mapping: FieldMapping;
  sourceField?: ConnectorField;
  destField?: ConnectorField;
  writableFields: ConnectorField[];
  availableSources: ConnectorField[];
  onDestinationChange: (destinationField: string | null) => void;
  onIgnore: () => void;
  onSourceChange: (sourceField: string) => void;
}) {
  const destSelectId = useId();
  const sourceSelectId = useId();
  const isRequiredSlot = mapping.status === "required" && !mapping.sourceField;
  const label =
    sourceField?.label ?? destField?.label ?? (mapping.sourceField || "Field");
  const sourceOptions = useMemo(() => {
    const current = sourceField ? [sourceField] : [];
    const keys = new Set(current.map((f) => f.key));
    return [...current, ...availableSources.filter((f) => !keys.has(f.key))];
  }, [availableSources, sourceField]);

  return (
    <div
      className={cn(
        "rs-wizard-mapping-row",
        mapping.status === "required" &&
          !mapping.ignored &&
          "rs-wizard-mapping-row-required",
        mapping.ignored && "opacity-60",
      )}
    >
      <div className="rs-wizard-mapping-source">
        <Label
          htmlFor={sourceSelectId}
          className="text-xs font-medium uppercase tracking-wide text-rs-ink-subtle"
        >
          Source Field
        </Label>
        <select
          id={sourceSelectId}
          className={selectClassName("mt-1")}
          value={mapping.sourceField}
          disabled={mapping.ignored}
          onChange={(e) => onSourceChange(e.target.value)}
        >
          {isRequiredSlot ? (
            <option value="">Select Source Field…</option>
          ) : null}
          {sourceOptions.map((f) => (
            <option key={f.key} value={f.key}>
              {f.label}
              {f.required ? " *" : ""}
            </option>
          ))}
        </select>
        {sourceField?.description ? (
          <p className="mt-1 text-xs text-rs-ink-subtle">
            {sourceField.description}
          </p>
        ) : null}
        {destField?.required ? (
          <p className="mt-0.5 text-xs text-rs-danger">
            Required Destination Field
          </p>
        ) : null}
      </div>

      <div className="rs-wizard-mapping-arrow" aria-hidden>
        →
      </div>

      <div className="rs-wizard-mapping-dest">
        <Label
          htmlFor={destSelectId}
          className="text-xs font-medium uppercase tracking-wide text-rs-ink-subtle"
        >
          Destination Field
        </Label>
        <div className="mt-1">
          <SearchableFieldSelect
            id={destSelectId}
            value={mapping.destinationField}
            options={writableFields}
            placeholder="Select Destination Field…"
            disabled={mapping.ignored}
            onChange={onDestinationChange}
          />
        </div>
      </div>

      <div className="rs-wizard-mapping-meta">
        <MappingStatus mapping={mapping} />
        {!isRequiredSlot ? (
          <SimpleTooltip
            content={
              mapping.ignored ? "Restore This Mapping" : "Ignore This Mapping"
            }
          >
            <button
              type="button"
              className="rounded-rs-sm p-1.5 text-rs-ink-subtle hover:bg-rs-muted hover:text-rs-ink"
              aria-label={
                mapping.ignored ? `Restore ${label}` : `Ignore ${label}`
              }
              onClick={onIgnore}
            >
              <X className="size-4" aria-hidden />
            </button>
          </SimpleTooltip>
        ) : null}
      </div>
    </div>
  );
}

export function WizardConfigureStep() {
  const { orgId, canManage } = useOrg();
  const navigate = useWizardNavigate();
  const headingId = useId();

  const initial = loadWizard(orgId);
  const sourceProvider = initial.source ?? "zoom";
  const destinationProvider = initial.destinationProvider;

  const [sourceSchema, setSourceSchema] = useState<ConnectorSchema | null>(
    null,
  );
  const [destinationSchema, setDestinationSchema] =
    useState<ConnectorSchema | null>(null);
  const [schedule, setSchedule] = useState<SyncScheduleConfig>(
    () =>
      initial.configure?.schedule ??
      defaultSchedule({ supportsEventTriggeredSync: true }),
  );
  const [mappings, setMappings] = useState<FieldMapping[]>(
    () => initial.configure?.mappings ?? [],
  );
  const [behavior, setBehavior] = useState(
    () => initial.configure?.behavior ?? defaultBehavior(),
  );
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [discoveryWarnings, setDiscoveryWarnings] = useState<string[]>([]);
  const [authExpired, setAuthExpired] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [adding, setAdding] = useState(false);
  const [addSourceKey, setAddSourceKey] = useState("");

  async function loadSchemas() {
    if (!destinationProvider) {
      setLoading(false);
      setLoadError("Choose a destination before configuring sync.");
      return;
    }
    setLoading(true);
    setLoadError(null);
    setDiscoveryWarnings([]);
    setAuthExpired(false);
    try {
      const wizard = loadWizard(orgId);
      const event = wizard.event;
      const res = await api<{
        source: ConnectorSchema;
        destination: ConnectorSchema;
        discoveredAt?: string;
        warnings?: string[];
      }>(`/api/accounts/${encodeURIComponent(orgId)}/field-discovery`, {
        method: "POST",
        body: JSON.stringify({
          source: {
            provider: sourceProvider,
            connectionId: event?.connectionId,
            resourceType: event?.resourceType,
            resourceId: event?.id,
          },
          destination: {
            provider: destinationProvider,
            credentialId: wizard.destinationCredentialId ?? null,
          },
        }),
      });

      if (!res.source?.fields?.length) {
        setLoadError("This source does not expose any fields to map.");
        setSourceSchema(res.source ?? null);
        setDestinationSchema(res.destination ?? null);
        return;
      }
      if (!res.destination?.fields?.some((f) => f.writable !== false)) {
        setLoadError("This destination does not expose any writable fields.");
        setSourceSchema(res.source);
        setDestinationSchema(res.destination ?? null);
        return;
      }

      const warnings = res.warnings ?? [];
      setDiscoveryWarnings(warnings);
      setLoadError(null);
      setSourceSchema(res.source);
      setDestinationSchema(res.destination);

      const key = configureSchemaKey(
        sourceProvider,
        destinationProvider,
        event?.id,
        wizard.destinationCredentialId,
      );
      const saved = loadWizard(orgId).configure;
      const nextSchedule =
        saved?.schedule && saved.schemaKey === key
          ? saved.schedule
          : defaultSchedule(res.source.capabilities);

      const nextMappings =
        saved?.mappings?.length && saved.schemaKey === key
          ? reconcileFieldMappings(
              saved.mappings,
              res.source.fields,
              res.destination.fields,
              res.destination.recommendedMappings,
            )
          : saved?.mappings?.length
            ? reconcileFieldMappings(
                saved.mappings,
                res.source.fields,
                res.destination.fields,
                res.destination.recommendedMappings,
              )
            : proposeFieldMappings(
                res.source.fields,
                res.destination.fields,
                res.destination.recommendedMappings,
              );

      const strategies = availableMatchStrategies(
        res.source.fields,
        res.destination.fields,
      );
      const nextBehavior = {
        ...(saved?.behavior ?? defaultBehavior()),
      };
      if (!strategies.includes(nextBehavior.matchStrategy)) {
        nextBehavior.matchStrategy = strategies[0] ?? "email";
      }

      setSchedule(nextSchedule);
      setMappings(nextMappings);
      setBehavior(nextBehavior);

      const configure: WizardConfigureState = {
        schedule: nextSchedule,
        mappings: nextMappings,
        behavior: nextBehavior,
        schemaKey: key,
        discovery: {
          discoveredAt: res.discoveredAt,
          sourceDiscovery: res.source.discoverySource,
          destinationDiscovery: res.destination.discoverySource,
          warnings,
        },
      };
      saveWizard(orgId, { ...loadWizard(orgId), configure });
    } catch (e) {
      const message =
        e instanceof Error ? e.message : "Could not discover field schemas";
      if (/401|unauth|session|expired/i.test(message)) {
        setAuthExpired(true);
        setLoadError("Your session expired. Sign in again, then retry.");
      } else {
        setLoadError(message);
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadSchemas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    orgId,
    sourceProvider,
    destinationProvider,
    initial.event?.id,
    initial.event?.connectionId,
    initial.destinationCredentialId,
  ]);

  function persist(next: Partial<WizardConfigureState>) {
    const wizard = loadWizard(orgId);
    const configure: WizardConfigureState = {
      schedule: next.schedule ?? schedule,
      mappings: next.mappings ?? mappings,
      behavior: next.behavior ?? behavior,
      schemaKey: configureSchemaKey(
        sourceProvider,
        destinationProvider,
        wizard.event?.id,
        wizard.destinationCredentialId,
      ),
      discovery: next.discovery ?? wizard.configure?.discovery,
    };
    if (next.schedule) setSchedule(next.schedule);
    if (next.mappings) setMappings(next.mappings);
    if (next.behavior) setBehavior(next.behavior);
    saveWizard(orgId, { ...wizard, configure });
  }

  const writableFields = useMemo(
    () => (destinationSchema?.fields ?? []).filter((f) => f.writable !== false),
    [destinationSchema],
  );

  const sourceByKey = useMemo(() => {
    const map = new Map<string, ConnectorField>();
    for (const f of sourceSchema?.fields ?? []) map.set(f.key, f);
    return map;
  }, [sourceSchema]);

  const destByKey = useMemo(() => {
    const map = new Map<string, ConnectorField>();
    for (const f of destinationSchema?.fields ?? []) map.set(f.key, f);
    return map;
  }, [destinationSchema]);

  const matchStrategies = useMemo(() => {
    if (!sourceSchema || !destinationSchema) return [] as MatchStrategy[];
    return availableMatchStrategies(
      sourceSchema.fields,
      destinationSchema.fields,
    );
  }, [sourceSchema, destinationSchema]);

  const validation =
    sourceSchema && destinationSchema
      ? validateConfigureSync({
          schedule,
          mappings,
          behavior,
          sourceSchema,
          destinationSchema,
        })
      : null;

  const visibleMappings = mappings.filter(
    (m) => m.sourceField || m.status === "required",
  );

  const unusedSources = (sourceSchema?.fields ?? []).filter(
    (f) => !mappings.some((m) => m.sourceField === f.key && !m.ignored),
  );

  if (!canManage) {
    return (
      <p className="text-sm text-rs-danger">
        Only owners and admins can configure sync.
      </p>
    );
  }

  if (!destinationProvider || !initial.destinationConfigured) {
    return (
      <div className="rs-connect-step">
        <div className="rs-wizard-select-main">
          <div className="rs-wizard-select-header">
            <h2 className="text-rs-ink">Configure Sync</h2>
            <p className="text-sm text-rs-ink-muted">
              Finish choosing and connecting a destination before configuring
              this sync.
            </p>
          </div>
        </div>
        <WizardStepFooter
          onBack={() => navigate("/conferences/new/destinations")}
          primary={
            <Button onClick={() => navigate("/conferences/new/destinations")}>
              Set Up Destination
            </Button>
          }
        />
      </div>
    );
  }

  function continueToReview() {
    setShowErrors(true);
    if (!validation?.ok) return;
    persist({});
    navigate("/conferences/new/review");
  }

  return (
    <div className="rs-connect-step">
      <div className="rs-wizard-configure-main">
        <div className="rs-wizard-select-header">
          <h2 id={headingId} className="text-rs-ink">
            Configure Sync
          </h2>
          <p className="text-sm text-rs-ink-muted">
            Choose when this sync runs and how attendee data should be mapped
            from your source to your destination.
          </p>
        </div>

        <div className="rs-wizard-configure-scroll">
          {loading ? (
            <div className="space-y-3">
              <Skeleton className="h-28 w-full" />
              <Skeleton className="h-48 w-full" />
              <Skeleton className="h-32 w-full" />
            </div>
          ) : loadError && !sourceSchema && !destinationSchema ? (
            <div className="rs-wizard-event-empty space-y-3">
              <p className="text-sm text-rs-danger">{loadError}</p>
              {authExpired ? (
                <Button variant="secondary" onClick={() => navigate("/login")}>
                  Sign In
                </Button>
              ) : (
                <Button variant="secondary" onClick={() => void loadSchemas()}>
                  Retry
                </Button>
              )}
            </div>
          ) : sourceSchema && destinationSchema ? (
            <div className="rs-wizard-configure-sections">
              {loadError ? (
                <p role="alert" className="text-sm text-rs-warning">
                  {loadError}
                </p>
              ) : null}

              <ScheduleSection
                schedule={schedule}
                sourceSchema={sourceSchema}
                errors={showErrors ? (validation?.scheduleErrors ?? []) : []}
                onChange={(next) => persist({ schedule: next })}
              />

              <section
                className="rs-wizard-configure-section"
                aria-labelledby="configure-mapping-heading"
              >
                <h3 id="configure-mapping-heading">Map Attendee Data</h3>
                <p className="text-sm text-rs-ink-muted">
                  Match each source field to a destination field. Auto-matched
                  rows are a starting point — change either side to remap, or
                  ignore fields you do not want to sync.
                </p>
                <div className="rs-wizard-mapping-providers">
                  <span>
                    <strong className="text-rs-ink">Source</strong>
                    <span className="mt-0.5 block text-rs-ink-muted">
                      {sourceSchema.label}
                      {sourceSchema.discoverySource === "live"
                        ? " · Live Poll"
                        : " · Catalog"}
                      {` · ${sourceSchema.fields.length} fields`}
                    </span>
                  </span>
                  <span>
                    <strong className="text-rs-ink">Destination</strong>
                    <span className="mt-0.5 block text-rs-ink-muted">
                      {destinationSchema.label}
                      {destinationSchema.discoverySource === "live"
                        ? " · Live Poll"
                        : " · Catalog"}
                      {` · ${writableFields.length} writable`}
                    </span>
                  </span>
                </div>
                {discoveryWarnings.length > 0 ? (
                  <ul
                    className="mt-2 space-y-1 text-sm text-rs-warning"
                    role="status"
                  >
                    {discoveryWarnings.map((warning) => (
                      <li key={warning}>{warning}</li>
                    ))}
                  </ul>
                ) : null}

                <div className="rs-wizard-mapping-list">
                  {visibleMappings.length === 0 ? (
                    <p className="text-sm text-rs-ink-muted">
                      No fields available to map.
                    </p>
                  ) : (
                    visibleMappings.map((mapping, index) => (
                      <MappingRow
                        key={`${mapping.sourceField}:${mapping.destinationField}:${index}`}
                        mapping={mapping}
                        sourceField={
                          mapping.sourceField
                            ? sourceByKey.get(mapping.sourceField)
                            : undefined
                        }
                        destField={
                          mapping.destinationField
                            ? destByKey.get(mapping.destinationField)
                            : undefined
                        }
                        writableFields={writableFields}
                        availableSources={unusedSources}
                        onDestinationChange={(destinationField) => {
                          const next = mappings.map((m, i) => {
                            if (i !== index) return m;
                            if (!destinationField) {
                              return {
                                ...m,
                                destinationField: null,
                                status: "unmapped" as const,
                              };
                            }
                            return {
                              ...m,
                              destinationField,
                              ignored: false,
                              status: "matched" as const,
                            };
                          });
                          persist({ mappings: next });
                        }}
                        onSourceChange={(sourceField) => {
                          if (!sourceField) return;
                          const next = mappings.map((m, i) =>
                            i === index
                              ? {
                                  ...m,
                                  sourceField,
                                  status: m.destinationField
                                    ? ("matched" as const)
                                    : ("unmapped" as const),
                                }
                              : m,
                          );
                          persist({ mappings: next });
                        }}
                        onIgnore={() => {
                          const next = mappings.map((m, i) =>
                            i === index
                              ? {
                                  ...m,
                                  ignored: !m.ignored,
                                  destinationField: m.ignored
                                    ? m.destinationField
                                    : null,
                                  status: "unmapped" as const,
                                }
                              : m,
                          );
                          persist({ mappings: next });
                        }}
                      />
                    ))
                  )}
                </div>

                {adding ? (
                  <div className="mt-3 flex flex-wrap items-end gap-2">
                    <div className="min-w-[12rem] flex-1 space-y-1.5">
                      <Label htmlFor="configure-add-source">Source Field</Label>
                      <select
                        id="configure-add-source"
                        className={selectClassName()}
                        value={addSourceKey}
                        onChange={(e) => setAddSourceKey(e.target.value)}
                      >
                        <option value="">Select…</option>
                        {unusedSources.map((f) => (
                          <option key={f.key} value={f.key}>
                            {f.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <Button
                      variant="secondary"
                      disabled={!addSourceKey}
                      onClick={() => {
                        if (!addSourceKey) return;
                        persist({
                          mappings: [
                            ...mappings,
                            {
                              sourceField: addSourceKey,
                              destinationField: null,
                              status: "unmapped",
                            },
                          ],
                        });
                        setAddSourceKey("");
                        setAdding(false);
                      }}
                    >
                      Add
                    </Button>
                    <Button variant="ghost" onClick={() => setAdding(false)}>
                      Cancel
                    </Button>
                  </div>
                ) : (
                  <Button
                    variant="secondary"
                    className="mt-3"
                    disabled={unusedSources.length === 0}
                    onClick={() => setAdding(true)}
                  >
                    <Plus className="size-4" aria-hidden /> Add Mapping
                  </Button>
                )}

                {showErrors &&
                validation &&
                validation.mappingErrors.length > 0 ? (
                  <ul
                    className="mt-3 space-y-1 text-sm text-rs-danger"
                    role="alert"
                  >
                    {validation.mappingErrors.map((err) => (
                      <li key={err}>{err}</li>
                    ))}
                  </ul>
                ) : null}
              </section>

              <section
                className="rs-wizard-configure-section"
                aria-labelledby="configure-behavior-heading"
              >
                <h3 id="configure-behavior-heading">Sync Behavior</h3>
                <div className="space-y-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="configure-existing">
                      When a Matching Contact Already Exists
                    </Label>
                    <select
                      id="configure-existing"
                      className={selectClassName()}
                      value={behavior.existingRecordAction}
                      onChange={(e) =>
                        persist({
                          behavior: {
                            ...behavior,
                            existingRecordAction: e.target
                              .value as typeof behavior.existingRecordAction,
                          },
                        })
                      }
                    >
                      <option value="update">
                        Update the Existing Contact (Recommended)
                      </option>
                      <option value="skip">Skip the Attendee</option>
                      <option value="create">Create a New Record</option>
                    </select>
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="configure-match">
                      Match Existing Contacts By
                    </Label>
                    <select
                      id="configure-match"
                      className={selectClassName()}
                      value={behavior.matchStrategy}
                      onChange={(e) =>
                        persist({
                          behavior: {
                            ...behavior,
                            matchStrategy: e.target.value as MatchStrategy,
                          },
                        })
                      }
                    >
                      {matchStrategies.includes("email") ? (
                        <option value="email">Email Address</option>
                      ) : null}
                      {matchStrategies.includes("destination_id") ? (
                        <option value="destination_id">
                          Destination Record ID
                        </option>
                      ) : null}
                      {matchStrategies.includes("email_and_name") ? (
                        <option value="email_and_name">Email + Name</option>
                      ) : null}
                      {matchStrategies.includes("custom") ? (
                        <option value="custom">Custom Matching Rules</option>
                      ) : null}
                    </select>
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="configure-empty">
                      When Source Data Is Empty
                    </Label>
                    <select
                      id="configure-empty"
                      className={selectClassName()}
                      value={behavior.emptySourceValueAction}
                      onChange={(e) =>
                        persist({
                          behavior: {
                            ...behavior,
                            emptySourceValueAction: e.target
                              .value as typeof behavior.emptySourceValueAction,
                          },
                        })
                      }
                    >
                      <option value="preserve">
                        Keep the Existing Destination Value
                      </option>
                      <option value="clear">Clear the Destination Value</option>
                    </select>
                    <p className="text-xs text-rs-ink-subtle">
                      Empty source values do not overwrite CRM data unless you
                      choose to clear them.
                    </p>
                  </div>

                  {showErrors &&
                  validation &&
                  validation.behaviorErrors.length > 0 ? (
                    <ul
                      className="space-y-1 text-sm text-rs-danger"
                      role="alert"
                    >
                      {validation.behaviorErrors.map((err) => (
                        <li key={err}>{err}</li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              </section>
            </div>
          ) : null}
        </div>
      </div>

      <WizardStepFooter
        onBack={() => navigate("/conferences/new/destinations")}
        primary={
          <Button
            disabled={loading || !sourceSchema || !destinationSchema}
            onClick={continueToReview}
          >
            Continue
          </Button>
        }
      />
    </div>
  );
}
